import { apiClient } from '@/shared/lib/api/apiClient';
import type { LoginResponse } from '@/shared/types/types';
import type {
  AccountModel,
  PlatformOverview,
  RangeKey,
  TenantListParams,
  TenantsPage,
  TenantDetail,
  PaginationParams,
  InstallationTokenOption,
} from '../types';

function paginationToParams(params: PaginationParams): Record<string, string> {
  const out: Record<string, string> = {};
  if (params.search) out.search = params.search;
  if (params.page) out.page = String(params.page);
  if (params.pageSize) out.pageSize = String(params.pageSize);
  return out;
}

export const administrationApi = {
  getOverview(range: RangeKey, from?: string, to?: string, scope?: string): Promise<PlatformOverview> {
    const params: Record<string, string> = { range };
    if (range === 'custom') {
      if (from) params.from = from;
      if (to) params.to = to;
    }
    if (scope) params.scope = scope;
    return apiClient.get<PlatformOverview>('/admin/platform/overview', params);
  },
  getTenants(params: TenantListParams): Promise<TenantsPage> {
    const query = paginationToParams(params);
    if (params.type && params.type !== 'todos') query.type = params.type;
    if (params.rootsOnly) query.rootsOnly = 'true';
    if (params.parentId) query.parentId = params.parentId;
    if (params.subtreeOf) query.subtreeOf = params.subtreeOf;
    return apiClient.get<TenantsPage>('/admin/platform/tenants', query);
  },
  getTenant(id: string): Promise<TenantDetail> {
    return apiClient.get<TenantDetail>(`/admin/platform/tenants/${id}`);
  },
  // Acting view (Fase A): o backend devolve uma sessao nova (LoginResponse)
  // com os claims da conta assumida; aplicar via authService.completeLogin.
  assumeView(tenantId: string): Promise<LoginResponse> {
    return apiClient.put<LoginResponse>('/admin/platform/acting-view', { tenantId });
  },
  releaseView(): Promise<LoginResponse> {
    return apiClient.delete<LoginResponse>('/admin/platform/acting-view');
  },
  moveTenant(id: string, body: { newParentId: string | null; installationTokenId: string | null }): Promise<{ movedLicenses: number }> {
    return apiClient.post<{ movedLicenses: number }>(`/admin/platform/tenants/${id}/move`, body);
  },
  getTenantInstallationTokens(id: string): Promise<InstallationTokenOption[]> {
    return apiClient.get<InstallationTokenOption[]>(`/admin/platform/tenants/${id}/installation-tokens`);
  },
  resetUserPassword(tenantId: string, userId: string): Promise<void> {
    return apiClient.post<void>(`/admin/platform/tenants/${tenantId}/users/${userId}/reset-password`);
  },
  resendUserInvite(tenantId: string, userId: string): Promise<void> {
    return apiClient.post<void>(`/admin/platform/tenants/${tenantId}/users/${userId}/resend-invite`);
  },
  resetUserMfa(tenantId: string, userId: string, code: string): Promise<void> {
    return apiClient.post<void>(
      `/admin/platform/tenants/${tenantId}/users/${userId}/reset-mfa`,
      { code },
    );
  },
  changeTenantType(id: string, targetModel: AccountModel, password: string): Promise<void> {
    return apiClient.patch<void>(`/admin/platform/tenants/${id}/type`, { targetModel, password });
  },
  createTenant(payload: { nome: string; accountModel: AccountModel; adminNome: string; adminEmail: string; document?: string | null; legalName?: string | null }): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>('/admin/platform/tenants', payload);
  },
  setTenantSubdomain(id: string, subdomain: string | null): Promise<void> {
    return apiClient.patch<void>(`/admin/platform/tenants/${id}/subdomain`, { subdomain });
  },
  setTenantStatus(id: string, ativo: boolean): Promise<void> {
    return apiClient.patch<void>(`/admin/platform/tenants/${id}/status`, { ativo });
  },
  deleteTenant(id: string): Promise<void> {
    return apiClient.delete<void>(`/admin/platform/tenants/${id}`);
  },
};
